import React, { useRef, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Link, Typography } from '@mui/material';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import { AuthCard, AuthLayout } from './pagekit';
import { isUnknownTokenError, updateSubscriptionByToken } from '../services/emailPreferencesService';

/** Public route of the unsubscribe page (no sign-in needed; linked from marketing emails). */
export const UNSUBSCRIBE_PATH = '/unsubscribe';

/** Copy used on the page (kept in one place so tests can check it has no em dashes). */
export const UNSUBSCRIBE_COPY = Object.freeze({
  confirmTitle: 'Unsubscribe from Quill AI product emails?',
  confirmBody: "You'll stop getting product updates and tips from Quill AI.",
  transactional: 'Invites, password resets and billing emails will still arrive.',
  unsubscribedTitle: "You're unsubscribed",
  resubscribedTitle: "You're subscribed again",
  resubscribedBody: "Thanks for sticking around. We'll keep product emails short and occasional.",
  missingTitle: 'This link is incomplete',
  missingBody:
    'Open the unsubscribe link from your email again. You can also sign in and turn off product emails in Settings.',
  unknownTitle: "We couldn't find that link",
  unknownBody:
    'This unsubscribe link is not valid. Open the link from your most recent email, or sign in and turn off product emails in Settings.',
  resubscribeBlocked: "We can't turn product emails back on for this address. Contact info@quillai.app.",
});

/**
 * Analytics beforeSend hook (Vercel Analytics and Speed Insights): the unsubscribe token in
 * `/unsubscribe?t=...` is a bearer credential, so it never leaves the browser in a beacon.
 * Other URLs pass through unchanged.
 * @param {{url?: string}|null} event - Analytics event.
 * @returns {object|null} Event to send (token removed), or null to drop it.
 */
export function scrubUnsubscribeToken(event) {
  if (!event || typeof event.url !== 'string') return event;
  let url;
  try {
    url = new URL(event.url, 'https://www.quillai.app');
  } catch {
    // Unparseable: drop it if it could carry the token.
    return /unsubscribe/i.test(event.url) ? null : event;
  }
  if (url.pathname.replace(/\/+$/, '') !== UNSUBSCRIBE_PATH || !url.searchParams.has('t')) return event;
  url.searchParams.delete('t');
  const absolute = /^[a-z][a-z0-9+.-]*:/i.test(event.url);
  const cleaned = absolute ? url.toString() : `${url.pathname}${url.search}${url.hash}`;
  return { ...event, url: cleaned };
}

/**
 * Masked email sentence, or a generic one when the server did not return an address.
 * @param {string|null} email - Masked email (e.g. j***@domain.com).
 * @param {boolean} subscribed - Whether the address is subscribed now.
 * @returns {string} Sentence.
 */
function emailSentence(email, subscribed) {
  if (subscribed) {
    return email ? `We'll send product emails to ${email} again.` : "We'll send you product emails again.";
  }
  return email ? `We won't send product emails to ${email} anymore.` : "We won't send you product emails anymore.";
}

/**
 * Public page reached from the Unsubscribe link in Quill AI marketing emails. Reads the `t`
 * token, asks before unsubscribing (link scanners prefetch pages, so nothing changes on load),
 * then offers Resubscribe.
 * @returns {JSX.Element} Page.
 */
export default function UnsubscribePage() {
  const [searchParams] = useSearchParams();
  const token = (searchParams.get('t') || '').trim();
  // 'confirm' | 'unsubscribed' | 'subscribed' | 'unknown'
  const [view, setView] = useState('confirm');
  const [email, setEmail] = useState(null);
  const [pending, setPending] = useState(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  // Re-entry guard: buttons stay enabled (aria-disabled) so focus is not lost while a call runs.
  const pendingRef = useRef(false);

  const run = async (action) => {
    if (pendingRef.current) return;
    pendingRef.current = true;
    setPending(action);
    setError('');
    setNotice('');
    try {
      const result = await updateSubscriptionByToken(token, action);
      setEmail(result.email);
      setView(result.status === 'unsubscribed' ? 'unsubscribed' : 'subscribed');
      // Resubscribe refused (a spam complaint or manual block stays in place): say so.
      if (action === 'resubscribe' && result.status === 'unsubscribed') setNotice(UNSUBSCRIBE_COPY.resubscribeBlocked);
    } catch (err) {
      if (isUnknownTokenError(err)) setView('unknown');
      else setError(err?.message || 'Something went wrong. Please try again.');
    } finally {
      pendingRef.current = false;
      setPending(null);
    }
  };

  const busy = pending !== null;
  const spinner = <CircularProgress size={18} color="inherit" aria-hidden="true" />;
  const homeLink = (
    <Typography variant="body2" sx={{ mt: 3, textAlign: 'center' }}>
      <Link component={RouterLink} to="/">
        Go to Quill AI
      </Link>
    </Typography>
  );
  const errorAlert = error ? (
    <Alert severity="error" sx={{ mb: 2 }} role="alert">
      {error}
    </Alert>
  ) : notice ? (
    <Alert severity="warning" sx={{ mb: 2 }} role="status">
      {notice}
    </Alert>
  ) : null;

  let card;
  if (!token || view === 'unknown') {
    const missing = !token;
    card = (
      <AuthCard
        title={missing ? UNSUBSCRIBE_COPY.missingTitle : UNSUBSCRIBE_COPY.unknownTitle}
        subtitle={missing ? UNSUBSCRIBE_COPY.missingBody : UNSUBSCRIBE_COPY.unknownBody}
      >
        <Button component={RouterLink} to="/login" variant="contained" fullWidth sx={{ textTransform: 'none' }}>
          Sign in to Quill AI
        </Button>
        {homeLink}
      </AuthCard>
    );
  } else if (view === 'unsubscribed') {
    card = (
      <AuthCard title={UNSUBSCRIBE_COPY.unsubscribedTitle} subtitle={emailSentence(email, false)}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, textAlign: 'center' }}>
          {UNSUBSCRIBE_COPY.transactional} Changed your mind?
        </Typography>
        {errorAlert}
        <Button
          variant="outlined"
          fullWidth
          onClick={() => run('resubscribe')}
          aria-disabled={busy ? 'true' : undefined}
          startIcon={pending === 'resubscribe' ? spinner : null}
          sx={{ textTransform: 'none' }}
        >
          {pending === 'resubscribe' ? 'Resubscribing...' : 'Resubscribe'}
        </Button>
        {homeLink}
      </AuthCard>
    );
  } else if (view === 'subscribed') {
    card = (
      <AuthCard title={UNSUBSCRIBE_COPY.resubscribedTitle} subtitle={emailSentence(email, true)}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, textAlign: 'center' }}>
          {UNSUBSCRIBE_COPY.resubscribedBody}
        </Typography>
        {errorAlert}
        <Button
          variant="text"
          fullWidth
          onClick={() => run('unsubscribe')}
          aria-disabled={busy ? 'true' : undefined}
          startIcon={pending === 'unsubscribe' ? spinner : null}
          sx={{ textTransform: 'none' }}
        >
          {pending === 'unsubscribe' ? 'Unsubscribing...' : 'Unsubscribe'}
        </Button>
        {homeLink}
      </AuthCard>
    );
  } else {
    card = (
      <AuthCard title={UNSUBSCRIBE_COPY.confirmTitle} subtitle={UNSUBSCRIBE_COPY.confirmBody}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, textAlign: 'center' }}>
          {UNSUBSCRIBE_COPY.transactional}
        </Typography>
        {errorAlert}
        <Button
          variant="contained"
          fullWidth
          onClick={() => run('unsubscribe')}
          aria-disabled={busy ? 'true' : undefined}
          startIcon={pending === 'unsubscribe' ? spinner : null}
          sx={{ textTransform: 'none' }}
        >
          {pending === 'unsubscribe' ? 'Unsubscribing...' : 'Unsubscribe'}
        </Button>
        {homeLink}
      </AuthCard>
    );
  }

  return (
    <AuthLayout>
      <Box aria-live="polite">{card}</Box>
    </AuthLayout>
  );
}
